import React, { useEffect, useState } from "react";
import { getS3URL } from "../../_helpers";
import "./ExploreProgram.scss";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment/moment";
import { useSelector, useDispatch } from "react-redux";
import CartButton from "../Courses/CourseTile/CartButton/CartButton";
import Share from "../Courses/CourseShare/courseShare";
import { Heart, Share2 } from "lucide-react";
import { deleteWishlist, addWishlist } from "../../_actions/cart.actions";
import { courseActions } from "../../_actions";

const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w\-]+/g, "")
    .replace(/\-\-+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
};

const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

const getShortDescription = (course) => {
  if (course.courseOneLiner) return course.courseOneLiner;
  const plainText = stripHtml(course.Description);
  return plainText.length > 120 ? plainText.substring(0, 120) + "..." : plainText;
};

function ExploreProgram() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { categories, isLoading, error } = useSelector(state => state?.category);
  const { courses, isFetching } = useSelector(state => state?.course || []);
  const { loggedIn } = useSelector(state => state?.authentication || {});
  const courseList = courses?.all?.courses || [];
  const [filterCourseData, setFilterCourseData] = useState([]);
  const [activeTab, setActiveTab] = useState();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [shareCourse, setShareCourse] = useState(null);
  
  useEffect(() => {
    // Sort categories and set initial active tab
    const sortedCategories = categories
      ?.slice()
      .sort((a, b) => a.categoryName.localeCompare(b.categoryName));

    // Set first category as active tab
    const initialCategory = sortedCategories?.[0]?.categoryName;
    setActiveTab(initialCategory);

    // Filter courses for the first category
    const initialFilteredCourses = courseList.filter(
      (data) => data.category === initialCategory
    );
    setFilterCourseData(initialFilteredCourses);
    // eslint-disable-next-line
  }, [categories, courseList]);

  const handleTabClick = (program) => {
    setActiveTab(program.categoryName);
    const filteredCourses = courseList.filter(
      (data) => data.category === program.categoryName
    );
    setFilterCourseData(filteredCourses);
    setIsSidebarOpen(false); // Close sidebar on mobile after selection
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleViewProgram = (course) => {
    const slug = slugify(course.CourseName);
    const id = course.ID;
    // navigate(`/${slug}/${id}`, { state: { course: course } });
    navigate(`/${slug}`, { state: { course: course } });
  };

  const likeHandler = (e, courseId, isWishlisted) => {
    e.stopPropagation(); // Prevent card navigation
    if (loggedIn) {
      dispatch(courseActions.handleResetCourseList());
      if (isWishlisted) {
        dispatch(deleteWishlist(courseId));
      } else {
        dispatch(addWishlist(courseId));
      }
    } 
    else {
      navigate("/login");
    }
  };

  // Sort categories for sidebar display
  const sortedCategories = categories
    ?.slice()
    .sort((a, b) => a.categoryName.localeCompare(b.categoryName));

  return (
    <>
      <div className="explore-container1">
        <div className="program-heading" style={{ display: "flex" }}>
          <button
            className="mobile-menu-button button"
            onClick={toggleSidebar}
            style={{ backgroundColor: "#3E3F41" }}
          >
            ☰
          </button>
          <h1>
            {t("ExploreProgram.Explore Our Top Programs")}
          </h1>
        </div>
        <div className="explore-container">
          <div className={`sidebar ${isSidebarOpen ? "active" : ""}`}>
            {sortedCategories?.map((program) => (
              <button
                key={program.id}
                className={`tab-button ${activeTab === program.categoryName ? "active" : ""}`}
                onClick={() => handleTabClick(program)}
              >
                {program.categoryName}
              </button>
            ))}
          </div>

          <div className="main-content scrollbar-thin">
            <div className="courses-grid">
              {filterCourseData?.map((course) => {
                const partnerName = course.companyName || 
                  (course.CourseName?.toLowerCase().includes('google') ? 'Google' : 
                   course.CourseName?.toLowerCase().includes('microsoft') ? 'Microsoft' : 
                   course.CourseName?.toLowerCase().includes('ibm') ? 'IBM' : 'SimkLearn');
                
                const partnerLogo = partnerName === 'Google' 
                  ? 'https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg'
                  : partnerName === 'Microsoft'
                  ? 'https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg'
                  : partnerName === 'IBM'
                  ? 'https://upload.wikimedia.org/wikipedia/commons/5/51/IBM_logo.svg'
                  : null;

                return (
                  <div
                    key={course?.id}
                    className="bg-white rounded-3xl p-4 shadow-md border border-gray-100/50 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 flex flex-col justify-between w-full h-[360px] cursor-pointer select-none text-gray-800"
                    onClick={() => handleViewProgram(course)}
                  >
                    <div>
                      {/* Banner Image */}
                      <div className="relative w-full h-32 rounded-2xl overflow-hidden mb-3 bg-zinc-100">
                        <img
                          src={course?.imageFiles?.[0]?.fileName ? getS3URL(course.imageFiles[0].fileName) : "https://pub-561e50d39a1c4c3a9c5d9a7715790c10.r2.dev/images/Adaptive.jpg"}
                          alt={course?.CourseName}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://pub-561e50d39a1c4c3a9c5d9a7715790c10.r2.dev/images/Adaptive.jpg";
                          }}
                        />
                        {partnerLogo && (
                          <div className="absolute top-2 left-2 bg-white p-1.5 rounded-lg shadow-sm flex items-center justify-center w-8 h-8">
                            <img src={partnerLogo} alt={partnerName} className="w-5 h-5 object-contain" />
                          </div>
                        )}

                        {/* Floating Overlay Controls */}
                        <div className="absolute top-2 right-2 flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                          <button
                            className={`w-7 h-7 rounded-full bg-white/95 backdrop-blur-sm border border-gray-100 flex items-center justify-center cursor-pointer shadow-sm hover:scale-110 active:scale-95 transition-all ${course?.isWishlisted ? "text-red-500" : "text-gray-500"}`}
                            onClick={(e) => likeHandler(e, course.ID, course?.isWishlisted)}
                            title={course?.isWishlisted ? "Remove from Wishlist" : "Add to Wishlist"}
                          >
                            <Heart size={13} fill={course?.isWishlisted ? "#ef4444" : "none"} stroke={course?.isWishlisted ? "#ef4444" : "currentColor"} />
                          </button>
                          <button
                            className="w-7 h-7 rounded-full bg-white/95 backdrop-blur-sm border border-gray-100 flex items-center justify-center cursor-pointer shadow-sm hover:scale-110 active:scale-95 transition-all text-gray-500"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShareCourse(course);
                            }}
                            title="Share Program"
                          >
                            <Share2 size={13} />
                          </button>
                        </div>
                      </div>

                      {/* Partner Name */}
                      <div className="flex items-center gap-1.5 mb-1 text-gray-500 text-xs font-semibold">
                        <span>{partnerName}</span>
                      </div>

                      {/* Title */}
                      <h4 className="font-extrabold text-sm text-gray-900 leading-snug line-clamp-2 hover:text-indigo-600 transition-colors">
                        {course?.CourseName}
                      </h4>
                    </div>

                    {/* Footer Details */}
                    <div className="pt-3 border-t border-gray-100 flex flex-col gap-1.5">
                      <span className="text-xs text-gray-400 font-medium">
                        {course?.courseType || 'Professional Certificate'}
                      </span>
                      
                      <div className="flex justify-between items-center gap-2 mt-1">
                        <div className="flex items-center gap-1 text-xs text-amber-500 font-bold">
                          <span>★</span>
                          <span>{course?.rating || '4.8'}</span>
                        </div>

                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleViewProgram(course)}
                            className="text-[10px] font-bold text-gray-600 hover:text-indigo-600 transition-colors px-2 py-1 rounded"
                          >
                            View
                          </button>
                          {loggedIn ? (
                            <CartButton post={course} />
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate("/login");
                              }}
                              className="bg-zinc-100 hover:bg-zinc-200 text-gray-800 text-[10px] font-extrabold px-3 py-1.5 rounded-lg transition-all"
                            >
                              {t("addToCart")}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {shareCourse && (
        <Share
          shareUrl={`${window.location.origin}/${slugify(shareCourse.CourseName)}`}
          handleClose={() => setShareCourse(null)}
        />
      )}
    </>
  );
}

export default ExploreProgram;
